import { FormattedMessage, defineMessages } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, BrandXL } from '@sorare/core/src/atoms/typography';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2026/NewSeasonLogo';
import { Season26TutorialDialog } from '@sorare/core/src/components/dialog/Season26TutorialDialog';
import { Season26TutorialStep } from '@sorare/core/src/components/dialog/Season26TutorialStep';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { CompleteMiletones } from './CompleteMilestones/index.tsx';
import { DailyMissionsStep } from './DailyMissionsStep/index.tsx';
import { WheelStep } from './WheelStep/index.tsx';

type Props = {
  onClose: () => void;
};

const messages = defineMessages({
  title: {
    id: 'CommonMissionsTutorialDialog.title',
    defaultMessage: 'Common Missions',
  },
});
const CommonMissionsTutorialDialogComponent = ({ onClose }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <Season26TutorialDialog
      open
      color="var(--c-blue-400)"
      textColor="var(--c-black)"
      titleGroup={
        isTabletAndAbove ? (
          <Vertical gap={4} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandM color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandM>
              <BrandXL className="text-center">
                <FormattedMessage {...messages.title} />
              </BrandXL>
            </Vertical>
          </Vertical>
        ) : (
          <Vertical gap={2} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandS>
              <BrandM className="text-center">
                <FormattedMessage {...messages.title} />
              </BrandM>
            </Vertical>
          </Vertical>
        )
      }
      items={[
        <Season26TutorialStep stepNb={1} key="1">
          <DailyMissionsStep />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={2} key="2">
          <WheelStep />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={3} key="3">
          <CompleteMiletones />
        </Season26TutorialStep>,
      ]}
      footer={
        <Horizontal center>
          <OnboardingButton size="large" color="primary" onClick={onClose}>
            <FormattedMessage {...glossary.letsGo} />
          </OnboardingButton>
        </Horizontal>
      }
    />
  );
};

export const CommonMissionsTutorialDialog = () => {
  const { lifecycle, update } = useLifecycle();
  if (lifecycle?.sawCommonMissionsTutorial) {
    return null;
  }
  return (
    <CommonMissionsTutorialDialogComponent
      onClose={() => {
        update(LIFECYCLE.sawCommonMissionsTutorial, true);
      }}
    />
  );
};
